import { PageSpinner } from '@armhr/ui';
import { Alert, Box, Card, Typography } from '@mui/material';
import { Helmet } from 'react-helmet';
import { useNavigate } from 'react-router-dom';

import { useApiData } from '../../hooks/useApiData';
import { useUser } from '../../hooks/useUser';
import { paths } from '../../utils/paths';

const isForbidden = (err: Error | null) =>
  (err as any)?.response?.status === 403;

const SwipeclockSchedulePtoPage: React.FC = () => {
  const { user } = useUser();
  const navigate = useNavigate();

  const ptoEnabled = !!user?.is_swipeclock_pto_enabled;
  const scheduleEnabled = !!user?.is_swipeclock_schedule_enabled;

  const {
    data: ptoUrl,
    loading: ptoLoading,
    error: ptoError,
  } = useApiData<string | null>((api) =>
    ptoEnabled
      ? api.attendance.getPtoRedirect()
      : Promise.resolve({ results: null })
  );

  const {
    data: scheduleUrl,
    loading: scheduleLoading,
    error: scheduleError,
  } = useApiData<string | null>((api) =>
    scheduleEnabled
      ? api.attendance.getScheduleRedirect()
      : Promise.resolve({ results: null })
  );

  // No SwipeClock, or the template disables both the PTO and schedule widgets.
  if (!user?.is_swipeclock_enabled || (!ptoEnabled && !scheduleEnabled)) {
    navigate(paths.dashboard);
    return null;
  }

  if ((ptoEnabled && ptoLoading) || (scheduleEnabled && scheduleLoading)) {
    return <PageSpinner />;
  }

  if (
    (ptoEnabled && isForbidden(ptoError)) ||
    (scheduleEnabled && isForbidden(scheduleError))
  ) {
    navigate(paths.dashboard);
    return null;
  }

  if (
    (ptoEnabled && (ptoError || !ptoUrl)) ||
    (scheduleEnabled && (scheduleError || !scheduleUrl))
  ) {
    return (
      <Alert severity="error">
        An error occured while loading the attendance page. Please refresh and
        try again
      </Alert>
    );
  }

  return (
    <Box sx={{ height: '100vh', display: 'flex', flexDirection: 'column' }}>
      <Helmet>
        <title>Schedule PTO | Armhr</title>
        <meta name="description" content="View your schedule and plan PTO." />
      </Helmet>
      <Box sx={{ display: 'flex', mb: 4 }}>
        <Typography variant="h2">Schedule PTO</Typography>
      </Box>
      {ptoEnabled && ptoUrl && (
        <iframe
          src={ptoUrl}
          title="SwipeClock"
          loading="lazy"
          style={{ width: '100%', height: 800, border: 'none' }}
        />
      )}
      {scheduleEnabled && scheduleUrl && (
        <Card>
          <iframe
            src={scheduleUrl}
            title="SwipeClock"
            loading="lazy"
            style={{ width: '100%', height: 750, border: 'none' }}
          />
        </Card>
      )}
    </Box>
  );
};

export default SwipeclockSchedulePtoPage;
